import { html } from 'lit';
import { combineHeadingTitle, highlightedHeading } from '../components/highlightedHeading.js';
import { type LandingAudienceHeroContent, type LandingContent } from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

type AudienceHeroKey = 'heroApplicant' | 'heroCompany';

const variantByKey: Record<AudienceHeroKey, 'applicant' | 'company'> = {
  heroApplicant: 'applicant',
  heroCompany: 'company',
};

const textNode = (
  editor: LandingEditorRenderOptions | undefined,
  elementId: string,
  baseClass: string,
  value: string,
) => html`
  <span
    class=${editorNodeClass(editor, elementId, baseClass)}
    style=${editorNodeStyle(editor, elementId)}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, elementId, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, elementId, event)}
    >${value}</span
  >
`;

const renderAudienceHero = (key: AudienceHeroKey, hero: LandingAudienceHeroContent, editor?: LandingEditorRenderOptions) => {
  if (!hero) return html``; // Failsafe
  
  const variant = variantByKey[key];

  return html`
    <section
      class=${editorNodeClass(editor, `section.${key}`, `vp-audience-hero vp-audience-hero--${variant}`)}
      style=${editorNodeStyle(editor, `section.${key}`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `section.${key}`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `section.${key}`, event)}
    >
      <div class="vp-shell">
        <div
          class=${editorNodeClass(editor, `${key}.copy`, 'vp-audience-hero-head')}
          style=${editorNodeStyle(editor, `${key}.copy`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.copy`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.copy`, event)}
        >
          <span class="vp-audience-hero-badge">
            ${hero.avatars.length
              ? html`<span class="vp-audience-hero-avatars">
                  ${hero.avatars.slice(0, 3).map(avatar => html`<img src=${avatar} alt="" loading="lazy" />`)}
                </span>`
              : ''}
            <span class="vp-audience-hero-badge-dot"></span>
            ${textNode(editor, `${key}.badge`, '', hero.badge)}
          </span>

          ${highlightedHeading({
            title: combineHeadingTitle(hero.title, hero.highlight),
            highlight: hero.highlight,
            className: 'vp-audience-hero-title',
            editor,
            titleElementId: `${key}.title`,
            highlightElementId: `${key}.highlight`,
          })}

          <p
            class=${editorNodeClass(editor, `${key}.intro`, 'vp-audience-hero-intro')}
            style=${editorNodeStyle(editor, `${key}.intro`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.intro`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.intro`, event)}
          >
            ${hero.intro}
          </p>

          <p class="vp-audience-hero-trust">
            <span class="vp-audience-hero-stars">
              ${[0, 1, 2, 3, 4].map(() => html`<span class="icon-[material-symbols--star] vp-audience-hero-star"></span>`)}
            </span>
            ${textNode(editor, `${key}.trustLabel`, '', hero.trustLabel)}
          </p>
        </div>

        <div
          class=${editorNodeClass(editor, `${key}.cards`, 'vp-audience-hero-cards')}
          style=${editorNodeStyle(editor, `${key}.cards`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.cards`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.cards`, event)}
        >
          ${hero.cards.map(
            (card, index) => html`
              <a
                class=${editorNodeClass(editor, `${key}.card.${index}`, 'vp-audience-card')}
                style=${editorNodeStyle(editor, `${key}.card.${index}`)}
                href=${card.href}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}`, event)}
                @click=${(event: MouseEvent) => {
                  if (editor?.mode === 'editor') {
                    event.preventDefault();
                    editorNodeClick(editor, `${key}.card.${index}`, event);
                  }
                }}
              >
                <img
                  class=${editorNodeClass(editor, `${key}.card.${index}.image`, 'vp-audience-card-image')}
                  style=${editorNodeStyle(editor, `${key}.card.${index}.image`)}
                  src=${card.image}
                  alt=${card.imageAlt}
                  loading="lazy"
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}.image`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.card.${index}.image`, event)}
                />
                <span class="vp-audience-card-body">
                  ${textNode(editor, `${key}.card.${index}.eyebrow`, 'vp-audience-card-eyebrow', card.eyebrow)}
                  ${textNode(editor, `${key}.card.${index}.title`, 'vp-audience-card-title', card.title)}
                </span>
                <span class="vp-audience-card-arrow">
                  <span class="icon-[material-symbols--arrow-outward]"></span>
                </span>
              </a>
            `,
          )}
        </div>
      </div>
    </section>
  `;
};

export const audienceHeroSection = (key: AudienceHeroKey, content: LandingContent, editor?: LandingEditorRenderOptions) =>
  renderAudienceHero(key, content[key], editor);
